import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Spinner,
} from '@/components/ui'
import { Button } from '@/components/ui'
import { Badge } from '@/components/ui'
import { PersonaFilters } from '@/components/PersonaFilters'
import { PersonaTagBadges } from '@/components/PersonaTagBadges'
import { usePersonaStore } from '@/stores'
import { useConversations, useFeature, usePersonaFilters } from '@/hooks'
import { useAuthStore } from '@/stores/authStore'
import { useSettingsStore } from '@/stores/settingsStore'
import {
  MessageSquare,
  Mic,
  Video,
  ArrowRight,
  Users,
} from 'lucide-react'
import type { Persona } from '@/stores'

export function HomePage() {
  const navigate = useNavigate()
  const { personas: personasMap, isLoading } = usePersonaStore()
  const { conversations, isLoading: isConversationsLoading, createConversation } = useConversations()
  const showVoice = useFeature('voice')
  const showAvatars = useFeature('avatars')
  const hasRedirected = useRef(false)

  // If conversations exist, redirect to the most recent one
  useEffect(() => {
    if (!isConversationsLoading && !hasRedirected.current && conversations.length > 0) {
      hasRedirected.current = true
      // Conversations are sorted by updatedAt descending, so first is most recent
      navigate(`/conversations/${conversations[0].id}`, { replace: true })
    }
  }, [conversations, isConversationsLoading, navigate])

  const hiddenPersonaIds = useSettingsStore((s) => s.ui.hiddenPersonaIds)
  // Hidden personas are simply absent on Home; unhide lives on /personas.
  const personaList = Object.values(personasMap).filter((p) => !hiddenPersonaIds[p.id])

  const currentUserId = useAuthStore((s) => s.user?.id)
  const {
    filters,
    filtered: visiblePersonas,
    tagUniverse,
    activeCount,
    setScope,
    toggleTag,
    toggleType,
    clearAll,
  } = usePersonaFilters(personaList, { currentUserId, persistToUrl: false })

  const getModeIcon = (mode: Persona['presentationMode']) => {
    switch (mode) {
      case 'chat':
        return MessageSquare
      case 'voice':
        return Mic
      case 'avatar':
        return Video
    }
  }

  const getModeLabel = (mode: Persona['presentationMode']) => {
    switch (mode) {
      case 'chat':
        return 'Chat'
      case 'voice':
        return 'Voice'
      case 'avatar':
        return 'Avatar'
    }
  }

  const handleStartConversation = async (personaId: string) => {
    const conversationId = await createConversation(personaId)
    navigate(`/conversations/${conversationId}`)
  }

  const handleStartWithoutPersona = async () => {
    const conversationId = await createConversation(null)
    navigate(`/conversations/${conversationId}`)
  }

  return (
    <PageFrame width="detail" className="space-y-8">
      <PageHeader
        title="Welcome Back"
        subtitle="Start a new conversation with one of your expert personas or chat without a specific persona."
      />

      {/* Loading State */}
      {(isLoading || isConversationsLoading) && (
        <div className="flex items-center justify-center py-12">
          <Spinner className="w-8 h-8" />
        </div>
      )}

      {/* Persona Cards Grid */}
      {!isLoading && !isConversationsLoading && personaList.length > 0 && (
        <div className="space-y-6">
          {personaList.length > 1 && (
            <PersonaFilters
              filters={filters}
              tagUniverse={tagUniverse}
              activeCount={activeCount}
              setScope={setScope}
              toggleTag={toggleTag}
              toggleType={toggleType}
              clearAll={clearAll}
              showVoice={showVoice}
              showAvatars={showAvatars}
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visiblePersonas.map((persona) => {
              const ModeIcon = getModeIcon(persona.presentationMode)

              return (
                <Card key={persona.id} className="flex flex-col transition-shadow hover:shadow-md cursor-pointer">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-3">
                      {persona.avatarUrl ? (
                        <img
                          src={persona.avatarUrl}
                          alt={persona.name}
                          className="w-14 h-14 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-linear-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground font-medium text-xl shrink-0">
                          {persona.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg">{persona.name}</CardTitle>
                        <CardDescription className="line-clamp-2 mt-1">
                          {persona.description || 'No description available'}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="py-2 flex-1">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="flex items-center gap-1">
                        <ModeIcon className="w-3 h-3" />
                        {getModeLabel(persona.presentationMode)}
                      </Badge>
                      {showVoice && persona.voiceConfig && (
                        <Badge variant="secondary">
                          {persona.voiceConfig.provider}
                        </Badge>
                      )}
                      {showAvatars && persona.avatarConfig && (
                        <Badge variant="secondary">
                          {persona.avatarConfig.provider}
                        </Badge>
                      )}
                      <PersonaTagBadges
                        tags={persona.tags}
                        activeTags={filters.tags}
                        onToggle={toggleTag}
                      />
                    </div>
                  </CardContent>

                  <CardFooter className="pt-3">
                    <Button
                      onClick={() => handleStartConversation(persona.id)}
                      className="w-full"
                      rightIcon={<ArrowRight className="w-4 h-4" />}
                    >
                      Start Conversation
                    </Button>
                  </CardFooter>
                </Card>
              )
            })}
          </div>
          {visiblePersonas.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              No personas match your filters.{' '}
              <button type="button" onClick={clearAll} className="text-primary underline">
                Clear filters
              </button>
            </p>
          )}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !isConversationsLoading && personaList.length === 0 && (
        <Card className="max-w-md mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              No Personas Yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Create your first expert persona to customize AI behavior for your conversations.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/personas')}
            >
              Create Persona
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Start without persona option */}
      {!isLoading && !isConversationsLoading && (
        <div className="flex justify-center pt-4">
          <Button
            variant="secondary"
            onClick={handleStartWithoutPersona}
            leftIcon={<MessageSquare className="w-4 h-4" />}
          >
            Start Without a Persona
          </Button>
        </div>
      )}
    </PageFrame>
  )
}
